import { useEffect, useId, useRef, useState } from "react";
import api from "../lib/api";
import { fmtDateTime, zonedParts, zonedDateTimeToIso } from "../lib/time";
import "../player-training-actions.css";

export function canRescheduleTraining(item) {
  // The server also checks feedback stored separately, automatic outcomes and
  // finalized score periods. Missing eligibility must never imply permission.
  return item?.player_reschedule_eligible === true && item.player_may_reschedule === true
    && ["training", "workout", "recovery"].includes(item.category)
    && ["planned", "upcoming", "overdue", "assumed_completed", "completed_auto", "missed"].includes(item.status)
    && item.training_unit_type !== "team_training" && item.training_snapshot?.training_unit_type !== "team_training"
    && !item.service_id && !item.provider_managed && !item.external_fixture_id
    && !item.training_feedback && !item.training_feedback_id && item.feedback_status !== "submitted"
    && !item.completed_at && !item.executed_at && !item.removed_from_plan
    && !["cancelled", "excluded"].includes(item.exception_type)
    && Number.isInteger(item.schedule_version) && item.schedule_version >= 1
    && Number.isFinite(Date.parse(item.original_start_at || ""))
    && Number.isFinite(Date.parse(item.start_at || ""));
}

export default function PlayerTrainingReschedule({ item, timezone, lang = "de", now, onSaved }) {
  const en = lang === "en", text = (de, english) => en ? english : de;
  const [open, setOpen] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState("");
  const [parts, setParts] = useState(() => zonedParts(item.start_at, timezone));
  const request = useRef(null), lock = useRef(false);
  const trigger = useRef(null), dateInput = useRef(null), formId = useId();
  useEffect(() => { if (open) dateInput.current?.focus(); }, [open]);
  if (!canRescheduleTraining(item)) return null;
  const close = () => { setOpen(false); trigger.current?.focus(); };
  const toggle = () => {
    if (open) { close(); return; }
    setParts(zonedParts(item.start_at, timezone)); setError(""); setOpen(true);
  };
  const submit = async event => {
    event.preventDefault();
    if (lock.current) return;
    let startAt;
    try { startAt = zonedDateTimeToIso(parts.date, parts.time, timezone); } catch { startAt = null; }
    // Reject nonexistent local wall times rather than silently normalizing a DST gap.
    const roundtrip = startAt && zonedParts(startAt, timezone);
    if (!startAt || roundtrip.date !== parts.date || roundtrip.time !== parts.time || Date.parse(startAt) <= (now ?? Date.now())) {
      setError(text("Bitte einen gültigen zukünftigen Termin wählen.", "Choose a valid future appointment.")); return;
    }
    const signature = `${item.id}:${item.schedule_version}:${startAt}`;
    if (request.current?.signature !== signature) request.current = { signature, key: globalThis.crypto.randomUUID() };
    lock.current = true; setBusy(true); setError("");
    try {
      await api.post(`/me/schedule-items/${encodeURIComponent(item.id)}/reschedule`, {
        start_at: startAt, expected_version: item.schedule_version,
        idempotency_key: request.current.key,
      });
      close();
      await onSaved?.(startAt);
    } catch (failure) {
      setError(failure.response?.status === 409
        ? text("Der Termin wurde inzwischen geändert. Bitte neu laden.", "This appointment changed. Please reload.")
        : text("Verschieben nicht möglich. Bitte Termin und Berechtigung prüfen.", "Unable to reschedule. Check the appointment and permission."));
    } finally { lock.current = false; setBusy(false); }
  };
  return <>
    <button ref={trigger} type="button" className="pc-btn pc-btn-ghost pc-player-reschedule-trigger" data-testid="player-training-reschedule"
      aria-expanded={open} aria-controls={open ? formId : undefined} disabled={busy} onClick={toggle}>{text("Training verschieben", "Reschedule training")}</button>
    {open && <form id={formId} aria-labelledby={`${formId}-title`} onSubmit={submit}
      onKeyDown={event => { if (event.key === "Escape" && !busy) { event.preventDefault(); event.stopPropagation(); close(); } }}
      className="pc-player-reschedule-form space-y-3 rounded-xl p-3" style={{ border: "1px solid var(--pc-border)" }}>
        <h4 id={`${formId}-title`} className="text-sm font-semibold">{text("Training verschieben", "Reschedule training")}</h4>
        <p className="text-xs" style={{ color: "var(--pc-muted)" }}>{text("Ursprünglicher Termin", "Original appointment")}: {fmtDateTime(item.original_start_at, timezone, lang)} · {timezone}</p>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs min-w-0">{text("Datum", "Date")}<input ref={dateInput} required type="date" className="pc-input w-full min-w-0 mt-1" value={parts.date} onChange={e => setParts(previous => ({ ...previous, date: e.target.value }))} onInput={e => { const date = e.currentTarget.value; setParts(previous => ({ ...previous, date })); }} /></label>
          <label className="text-xs min-w-0">{text("Uhrzeit", "Time")}<input required type="time" className="pc-input w-full min-w-0 mt-1" value={parts.time} onChange={e => setParts(previous => ({ ...previous, time: e.target.value }))} onInput={e => { const time = e.currentTarget.value; setParts(previous => ({ ...previous, time })); }} /></label>
        </div>
        <p className="text-xs" style={{ color: "var(--pc-muted)" }}>{text("Dauer und Inhalt bleiben erhalten. Dein Care Manager wird informiert.", "Duration and content stay the same. Your care manager will be notified.")}</p>
        {error && <p role="alert" className="text-xs">{error}</p>}
        <div className="flex flex-wrap gap-2"><button type="submit" className="pc-btn pc-btn-primary flex-1" disabled={busy}>{text("Speichern", "Save")}</button><button type="button" className="pc-btn pc-btn-ghost" disabled={busy} onClick={close}>{text("Abbrechen", "Cancel")}</button></div>
      </form>}
  </>;
}
